import { buildDemoDataset, buildDemoCurrentUser, DEMO_STORAGE_KEY, DEMO_CURRENT_USER_ID } from './demoDataset';

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
const MODE_STORAGE_KEY = 'pkk-training-demo-mode-v1';

const hasFirebaseConfig =
  Boolean(import.meta.env.VITE_FIREBASE_API_KEY) &&
  Boolean(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN) &&
  Boolean(import.meta.env.VITE_FIREBASE_PROJECT_ID);

const defaultDemoMode =
  import.meta.env.DEV ||
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  !hasFirebaseConfig;

const readModeOverride = () => {
  if (!isBrowser) return null;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored === 'demo' || stored === 'live' ? stored : null;
};

export const isDemoModeEnabled = () => {
  const override = readModeOverride();
  if (override === 'demo') return true;
  if (override === 'live') return hasFirebaseConfig ? false : true;
  return defaultDemoMode;
};

export const canUseLiveMode = () => hasFirebaseConfig;

export const setDemoModeOverride = (mode) => {
  if (!isBrowser) return;
  if (mode === 'demo' || mode === 'live') {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    return;
  }
  window.localStorage.removeItem(MODE_STORAGE_KEY);
};

export const getDemoModeOverride = () => readModeOverride();

let demoState = null;
let currentUser = null;
const listeners = new Set();

const clone = (value) => JSON.parse(JSON.stringify(value));

const loadState = () => {
  if (demoState) return demoState;

  if (!isBrowser) {
    demoState = buildDemoDataset();
    currentUser = buildDemoCurrentUser();
    return demoState;
  }

  const stored = window.localStorage.getItem(DEMO_STORAGE_KEY);
  if (stored) {
    try {
      demoState = JSON.parse(stored);
      currentUser = buildDemoCurrentUser();
      return demoState;
    } catch {
      window.localStorage.removeItem(DEMO_STORAGE_KEY);
    }
  }

  demoState = buildDemoDataset();
  currentUser = buildDemoCurrentUser();
  persistState();
  return demoState;
};

const persistState = () => {
  if (!isBrowser || !demoState) return;
  window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demoState));
};

const notify = () => {
  listeners.forEach((callback) => callback(currentUser ? clone(currentUser) : null));
};

const ensureState = () => loadState();

const getCollection = (collectionName) => {
  const state = ensureState();
  if (!Array.isArray(state[collectionName])) {
    state[collectionName] = [];
  }
  return state[collectionName];
};

const getFieldValue = (item, fieldPath) => {
  if (!fieldPath) return undefined;
  return String(fieldPath)
    .split('.')
    .reduce((accumulator, segment) => (accumulator == null ? undefined : accumulator[segment]), item);
};

const normalizeSortValue = (value) => {
  if (value == null) return value;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const applyWhere = (item, condition) => {
  const fieldPath = condition?._field?.segments?.join?.('.') || condition?._field?.canonicalString?.() || '';
  const op = condition?._op;
  const expected = condition?._value;
  const actual = getFieldValue(item, fieldPath);

  if (!op) return true;

  if (op === '==') return actual === expected;
  if (op === 'array-contains') return Array.isArray(actual) && actual.includes(expected);
  if (op === 'in') return Array.isArray(expected) && expected.includes(actual);
  if (op === '!=') return actual !== expected;
  return true;
};

const applyQuery = (items, conditions = []) => {
  const whereConditions = conditions.filter((condition) => condition?.type === 'where');
  const orderByConditions = conditions.filter((condition) => condition?.type === 'orderBy');
  const limitCondition = conditions.find((condition) => condition?.type === 'limit');

  let result = [...items];

  whereConditions.forEach((condition) => {
    result = result.filter((item) => applyWhere(item, condition));
  });

  if (orderByConditions.length > 0) {
    const { _field: field, _direction: direction } = orderByConditions[0];
    const fieldPath = field?.segments?.join?.('.') || '';
    result.sort((left, right) => {
      const leftValue = normalizeSortValue(getFieldValue(left, fieldPath));
      const rightValue = normalizeSortValue(getFieldValue(right, fieldPath));

      if (leftValue < rightValue) return direction === 'desc' ? 1 : -1;
      if (leftValue > rightValue) return direction === 'desc' ? -1 : 1;
      return 0;
    });
  }

  if (limitCondition?._limit) {
    result = result.slice(0, limitCondition._limit);
  }

  return result;
};

const setStateValue = (collectionName, nextItems) => {
  const state = ensureState();
  state[collectionName] = nextItems;
  persistState();
};

export const demoAuthService = {
  login: async () => {
    currentUser = buildDemoCurrentUser();
    notify();
    return clone(currentUser);
  },
  googleLogin: async () => {
    currentUser = buildDemoCurrentUser();
    notify();
    return clone(currentUser);
  },
  demoLogin: async () => {
    currentUser = buildDemoCurrentUser();
    notify();
    return clone(currentUser);
  },
  register: async () => {
    throw new Error('Rejestracja jest niedostępna w trybie demo.');
  },
  logout: async () => {
    currentUser = null;
    notify();
  },
  getCurrentUser: () => (currentUser ? clone(currentUser) : null),
  onAuthStateChanged: (callback) => {
    ensureState();
    const handler = (user) => callback(user);
    listeners.add(handler);
    Promise.resolve().then(() => handler(currentUser ? clone(currentUser) : null));
    return () => listeners.delete(handler);
  },
  isDemoUser: (uid) => uid === DEMO_CURRENT_USER_ID,
};

export const demoFirestoreService = {
  create: async (collectionName, data) => {
    const items = getCollection(collectionName);
    const id = data?.id || `${collectionName}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      id,
      ...clone(data),
      createdAt: data?.createdAt || new Date().toISOString(),
      updatedAt: data?.updatedAt || new Date().toISOString(),
    };
    items.push(payload);
    persistState();
    return clone(payload);
  },

  read: async (collectionName, docId) => {
    const items = getCollection(collectionName);
    const item = items.find((entry) => entry.id === docId || entry.uid === docId);
    return item ? clone(item) : null;
  },

  readAll: async (collectionName, conditions = []) => {
    const items = getCollection(collectionName);
    return clone(applyQuery(items, conditions));
  },

  update: async (collectionName, docId, data) => {
    const items = getCollection(collectionName);
    const index = items.findIndex((entry) => entry.id === docId || entry.uid === docId);
    if (index === -1) {
      throw new Error(`Nie znaleziono dokumentu ${collectionName}/${docId}`);
    }

    const nextItem = {
      ...items[index],
      ...clone(data),
      id: items[index].id,
      updatedAt: new Date().toISOString(),
    };

    items[index] = nextItem;
    persistState();
    return clone(nextItem);
  },

  delete: async (collectionName, docId) => {
    const items = getCollection(collectionName);
    const nextItems = items.filter((entry) => entry.id !== docId && entry.uid !== docId);
    setStateValue(collectionName, nextItems);
    return { id: docId, deleted: true };
  },

  query: () => null,
};

export const getDemoState = () => clone(ensureState());
export const setDemoCurrentUser = (user) => {
  currentUser = user ? clone(user) : null;
  notify();
};
export const resetDemoState = () => {
  demoState = buildDemoDataset();
  currentUser = buildDemoCurrentUser();
  persistState();
  notify();
};
